import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useMutation, UseQueryOptions } from '@tanstack/react-query';
import { useDispatch, useSelector } from 'react-redux';
import { get } from 'lodash';
import { useAtom } from 'jotai';
import { message } from 'antd';
import { getFilterFromRules, getRulesFromFilter } from '@utils';
import filterContainerSelector from '@store/filterContainer/selectors';
import { buildFilterSelector, isRTTrendsSelector } from '@store/trends/base/selectors';
import { pushDrilldownFilter } from '@store/dimensionContainer/actions';
import { resetDrillDown } from '@store/filterContainer/actions';
import { getFirstAppliedFilter, featureSelector, getUserProfile } from '@store/common/selectors';
import {
  filtersViewSelector as nocFiltersViewSelector,
  trendsViewSelector as nocTrendsViewSelector,
} from '@store/noc/base/selectors';
import {
  applyFiltersSelectedNocFilters,
  applyTrendsSelectedNocFilters,
} from '@store/noc/base/actions';
import {
  modeAtom,
  RESET_MODE_ATOM_VALUE,
  TABLE_MODES,
  useObserverResetModeValue,
} from '@components/dimension-panel-hz/atoms';
import * as commonTypes from '@store/common/types';
import * as filterTypes from '@store/filterContainer/types';
import * as apis from '@src/api/filters';
import { CURRENT_PRODUCT_MODE } from '@utils/constants';
import apptracker from '@utils/sensor/apptracker';
import * as Events from '@src/utils/sensor/track';
import { DataSource, type SavedFilter, type Filter, type FilterMeta } from '../components';
import type { FilterRule } from '../types';
import type * as Types from '../../filter/types';
import { checkCanEdit, convertToFilterInfo } from '../components/Management/SavedFilterList/utils';
import { convertCustomTagToDimension } from '../components/utils';

const modes = {
  [DataSource.video]: 'ei',
  [DataSource.ads]: 'ai',
  [DataSource.app]: 'app',
};

export const useFilterMeta = (
  dataSource: EnumToUnion<typeof DataSource>,
  options = { enabled: true } as UseQueryOptions<FilterMeta, unknown, FilterMeta, string[]>,
) => {
  return useQuery(
    ['get-filter-meta', dataSource],
    () => {
      const params = { mode: modes[dataSource] };
      if (dataSource === DataSource.app) {
        return apis.getAppMeta<FilterMeta>({ params });
      }
      return apis.getMeta<FilterMeta>({ params, ...apis.getV2AxiosConfig() });
    },
    {
      staleTime: 5 * 60 * 1000, // 5min
      refetchOnWindowFocus: false,
      enabled: options.enabled,
      ...options,
    },
  );
};

export const useGetSavedFilters = (
  dataSource: DataSource,
  options: UseQueryOptions<SavedFilter[], unknown, SavedFilter[], string[]>,
) => {
  return useQuery(
    ['get-saved-filters', dataSource],
    () => {
      const params = { mode: modes[dataSource] };
      if (dataSource === DataSource.app) {
        return apis.getFiltersForAppExperience<SavedFilter[]>({ params });
      }
      return apis.getFilters<SavedFilter[]>({ params, ...apis.getV2AxiosConfig() });
    },
    {
      refetchOnWindowFocus: false,
      enabled: options.enabled,
    },
  );
};

export const useEditFilter = () => {
  return useMutation(
    ({ id, filter, dataSource }: { id: number; filter: Filter; dataSource: DataSource }) => {
      const params = { mode: modes[dataSource] };
      if (dataSource === DataSource.app) {
        return apis.updateFilterForAppExperience(id, filter, { params });
      }
      return apis.updateFilter(id, filter, { params, ...apis.getV2AxiosConfig() });
    },
  );
};

export const useEnableFilter = () => {
  return useMutation(({ id, dataSource }: { id: number; dataSource: DataSource }) => {
    const params = { mode: modes[dataSource] };
    return apis.enableFilter(id, {
      params,
      ...apis.getV2AxiosConfig(),
    });
  });
};

export const useCreateFilter = () => {
  return useMutation(({ filter, dataSource }: { filter: Filter; dataSource: DataSource }) => {
    const params = { mode: modes[dataSource] };
    if (dataSource === DataSource.app) {
      return apis.createFilterForAppExperience<{ id: number; filter: SavedFilter }>(filter, {
        params,
      });
    }
    return apis.createFilter<{ id: number; filter: SavedFilter }>(filter, {
      params,
      ...apis.getV2AxiosConfig(),
    });
  });
};

export const useDeleteFilter = () => {
  return useMutation(({ ids, dataSource }: { ids: number[]; dataSource: DataSource }) => {
    const params = { mode: modes[dataSource] };

    if (dataSource === DataSource.app) {
      if (ids.length === 1) return apis.deleteFilterForAppExperience(ids[0], { params });

      return apis.deleteFiltersForAppExperience(ids, { params });
    }

    if (ids.length === 1) return apis.deleteFilter(ids[0], { params, ...apis.getV2AxiosConfig() });

    return apis.deleteFilters(ids, { params, ...apis.getV2AxiosConfig() });
  });
};

export const useDisabledDataSources = () => {
  const appEnabled = useSelector(featureSelector('app-experience')) as boolean;
  const disabledDataSources = useMemo(() => {
    return appEnabled ? [] : [DataSource.app];
  }, [appEnabled]) as DataSource[];
  return disabledDataSources;
};

const checkShouldFilterPopupUpdated = (dataSource: DataSource) => {
  const isVideoOrAdsFilterUpdated =
    ['EI', 'AI'].includes(CURRENT_PRODUCT_MODE) &&
    [DataSource.ads, DataSource.video].includes(dataSource);
  const isAppFilterUpdated = CURRENT_PRODUCT_MODE === 'APP' && dataSource === 'app';
  const shouldFilterPopupUpdated = isVideoOrAdsFilterUpdated || isAppFilterUpdated;

  return shouldFilterPopupUpdated;
};

// TODO: will be deprecated after filter popup is replaced by new filter search box
export const useNotifyFilterPopup = () => {
  const dispatch = useDispatch();
  const filterContainerState = useSelector(filterContainerSelector);
  const appliedFilter = useSelector(getFirstAppliedFilter);
  // Real-Time (NOC) keeps its own per-lane applied filters and only refetches
  // when one of its own actions fires. We patch those slices here so saved-filter
  // edits/deletes auto-propagate to RT, matching Trends behaviour.
  const nocFiltersView = useSelector(nocFiltersViewSelector);
  const nocTrendsView = useSelector(nocTrendsViewSelector);

  const syncNocAppliedFilters = (id: number, transform: (lane: any) => any | null) => {
    const patchLanes = (lanes: any[] = []) => {
      let changed = false;
      const next: any[] = [];
      lanes.forEach((lane) => {
        if (lane?.fid !== id) {
          next.push(lane);
          return;
        }
        const updated = transform(lane);
        if (updated === null) {
          changed = true;
          return;
        }
        next.push(updated);
        changed = true;
      });
      return { next, changed };
    };

    const filtersLanes = get(nocFiltersView, 'appliedFilter', []);
    const trendsLanes = get(nocTrendsView, 'appliedFilter', []);

    const filtersResult = patchLanes(filtersLanes);
    if (filtersResult.changed) {
      dispatch(applyFiltersSelectedNocFilters(filtersResult.next, undefined));
    }

    const trendsResult = patchLanes(trendsLanes);
    if (trendsResult.changed) {
      dispatch(applyTrendsSelectedNocFilters(trendsResult.next, undefined));
    }
  };

  return {
    onCreateNotify: (dataSource: DataSource, filter: SavedFilter) => {
      if (!checkShouldFilterPopupUpdated(dataSource)) return;
      try {
        // check if the filter is valid
        if (!filter || ['fid', 'category', 'name', 'rule'].some((key) => !filter[key])) return;
        const filters = get(filterContainerState, 'filters', []);
        const newFilters = [filter, ...filters];
        dispatch({
          type: filterTypes.DONE_GETTING_FILTERS,
          filters: newFilters,
        });
      } catch {
        /**/
      }
    },
    onDeleteNotify: (ids: number[], dataSource: DataSource) => {
      if (!checkShouldFilterPopupUpdated(dataSource)) return;

      try {
        const filters = get(filterContainerState, 'filters', []);
        if (!filters && !filters.length) return;

        const updatedFilters = filters.filter((item) => !ids.includes(item.fid));
        dispatch({
          type: filterTypes.DONE_GETTING_FILTERS,
          filters: updatedFilters,
        });

        // drop deleted saved filters from Real-Time lanes so RT stops querying
        // with a filter that no longer exists
        ids.forEach((id) => syncNocAppliedFilters(id, () => null));
      } catch {
        /**/
      }
    },
    onUpdateNotify: (id: number, filter: Filter, dataSource: DataSource) => {
      if (!checkShouldFilterPopupUpdated(dataSource)) return;

      try {
        const filters = get(filterContainerState, 'filters', []);
        if (!filters && !filters.length) return;
        const newFilters = [...filters];
        const targetFilter = newFilters.find((it) => it.fid === id);

        if (targetFilter) {
          targetFilter.category = `${filter.category}/${filter.subcategory}`;
          targetFilter.name = filter.name;
          targetFilter.rule = filter.rule;
          dispatch({
            type: filterTypes.DONE_GETTING_FILTERS,
            filters: newFilters,
          });
        }

        const isApplied = appliedFilter.fid === id;
        if (isApplied) {
          const tRules = getRulesFromFilter(filter.rule, true);
          const tFilter = getFilterFromRules(tRules, true);
          dispatch({
            type: commonTypes.APPLY_FILTER,
            filter: {
              name: filter.name,
              rule: filter.rule,
              fid: id,
            },
          });
          dispatch(resetDrillDown());
          dispatch(pushDrilldownFilter(filter.name || tFilter.name, tFilter.rule));
        }

        // propagate the edit to any Real-Time (NOC) lanes referencing this fid.
        // Each lane caches the rule snapshot; without this patch RT keeps
        // querying with the stale snapshot until the user removes + re-adds.
        syncNocAppliedFilters(id, (lane) => ({
          ...lane,
          name: filter.name,
          rule: filter.rule,
        }));
      } catch (e) {
        /**/
      }
    },
    onApplyNotify: (id: number, filter: Filter, dataSource: DataSource) => {
      if (!checkShouldFilterPopupUpdated(dataSource)) return;

      const tRules = getRulesFromFilter(filter.rule, true);
      const tFilter = getFilterFromRules(tRules, true);
      dispatch({
        type: commonTypes.APPLY_FILTER,
        filter: {
          name: filter.name,
          rule: filter.rule,
          fid: id,
        },
      });
      dispatch(resetDrillDown());
      dispatch(pushDrilldownFilter(filter.name || tFilter.name, tFilter.rule));

      window.dispatchEvent(
        new CustomEvent(RESET_MODE_ATOM_VALUE, {
          detail: {},
        }),
      );
    },
  };
};

export const useAdvancedFilterValue = () => {
  const { rule = [] } = useSelector(buildFilterSelector);
  const isRTTrends = useSelector(isRTTrendsSelector);
  const [mode, setMode] = useAtom(modeAtom);
  const firstRender = useRef(true);
  const advancedFilterEnabled = mode === TABLE_MODES.filter;
  const value = !isRTTrends && advancedFilterEnabled ? (rule as FilterRule[][]) : undefined;

  useObserverResetModeValue();

  useEffect(() => {
    if (!firstRender.current) {
      apptracker(
        advancedFilterEnabled
          ? Events.FILTER_BUILDER_EXIT_ADVANCED_FILTER_MODE
          : Events.FILTER_BUILDER_ENTER_ADVANCED_FILTER_MODE,
        {},
      );
    } else {
      firstRender.current = false;
    }
  }, [advancedFilterEnabled]);

  return [value, advancedFilterEnabled, setMode] as const;
};

export const useHandleEditFilterInitialize = ({ savedFilters, filterMeta }) => {
  const [editFilterId, setEditFilterId] = useState(null);
  const [filterEditor, setFilterEditor] = useState(null);
  useEffect(() => {
    if (savedFilters && filterMeta && editFilterId) {
      const filterDimensionSet = new Set(
        (
          filterMeta.dimensions.map((it) => it.fields).flat(Infinity) as Types.DimensionFieldMeta[]
        ).map((it) => it.value),
      );
      const filterToEdit = savedFilters.filter((filter) => filter.fid === editFilterId)[0];
      if (!filterToEdit) {
        message.info('This Filter Does Not Exist');
        return;
      }
      const convertedRule = convertCustomTagToDimension(filterDimensionSet, filterToEdit.rule);
      setFilterEditor(
        convertToFilterInfo({
          ...filterToEdit,
          rule: convertedRule,
        }),
      );
    }
  }, [filterMeta, savedFilters, editFilterId]);

  return { editFilterId, setEditFilterId, filterEditor, setFilterEditor };
};
